import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CircleCheckmark from '@/images/CircleCheckmark.svg';
import Location from '@/images/Location.svg';
import Mouse from '@/images/Mouse.svg';

import theme from '@/theme';
import { StyleCollection } from '@/utils/types';
import { ICampaign } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, DialogTitle, Typography } from '@mui/material';

const styles: StyleCollection = {
    closeIcon: theme => ({
        marginLeft: 'auto',
        color: theme.colors.primary,
    }),
    largeIcon: {
        width: '24px',
        height: '24px',
    },
    contentStyle: {
        overflow: 'hidden',
        alignItems: 'center',
        paddingBlockEnd: '24px',
    },
};

const StyledDialogTitle = styled(DialogTitle)`
    margin-block: 14px;
    margin-inline: 4px;
`;

const TableContainer = styled.div`
    display: flex;
    flex-direction: row;
    border-radius: 12px;
    border: 1px solid var(--Blacks-300, #dbdbdb);

    height: 154px;
    margin-inline: 4px;
`;

const CellContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 270px;
    justify-content: center;
    margin-inline-start: 24px;
    gap: 8px;
`;

const Count = styled.span`
    font-size: 28px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.56px;
`;

const IconContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const Text = styled.span`
    font-size: 16px;
    line-height: normal;
    letter-spacing: 0.15px;
`;

const VerticalDivider = styled.div`
    width: 1px;
    background-color: var(--Blacks-300, #dbdbdb);
    height: 100%;
`;

interface CellContentProps {
    count: number;
    icon: string;
    iconText: string;
}

const CellContent = ({ count, icon, iconText }: CellContentProps) => {
    return (
        <CellContainer>
            <IconContainer>
                <MaskedIcon src={icon} size="24px" color={theme.colors.secondary.main} />
                <Text>{iconText}</Text>
            </IconContainer>
            <Count>{count}</Count>
        </CellContainer>
    );
};

interface AnalyticsModalProps {
    campaign: ICampaign;
    handleClose: () => void;
    isOpen: boolean;
}

export const AnalyticsModal = ({ campaign, isOpen, handleClose }: AnalyticsModalProps) => {
    const { page_views, video_views, cta_clicks } = campaign;
    return (
        <BootstrapDialog
            onClose={handleClose}
            open={isOpen}
            contentstyle={styles.contentStyle}
            PaperProps={{ style: { borderRadius: '12px' } }}>
            <StyledDialogTitle id="campaigns-assets-modal">
                <Typography variant="h2">{campaign.title}</Typography>
                <IconButton ariaLabel="close" onClick={handleClose} sx={styles.closeIcon}>
                    <CloseIcon sx={styles.largeIcon} />
                </IconButton>
            </StyledDialogTitle>
            <DialogContent>
                <TableContainer>
                    <CellContent count={page_views} icon={Location} iconText={'Landed on page'} />
                    <VerticalDivider />
                    <CellContent count={video_views} icon={Mouse} iconText={'Clicked video'} />
                    <VerticalDivider />
                    <CellContent count={cta_clicks} icon={CircleCheckmark} iconText={'Clicked button'} />
                </TableContainer>
            </DialogContent>
        </BootstrapDialog>
    );
};
